import { ILockAndRollSymbolData } from '@GFTypes/GameStoreTypes';
import { doubleCoinIdsArray, encodeDoubleCoinSymbolId } from '../Data/DoubleLockAndRollCoinDataProcessor';

export const filterEmptyReels = (symbols: number[][]): number[][] => {
    return symbols.filter((reelSymbols) => !reelSymbols.every((symbolId) => symbolId === 0));
};

export const normalizeDoubleReels = (symbols: number[][], symbolMap: Map<number, [number, number]>): number[][] => {
    return symbols.map((reel, reelIndex, reels) => {
        const pairIndex = reelIndex % 2;
        const sourceReel = pairIndex === 0 ? reel : reels[reelIndex - 1];

        return sourceReel.map((symbolId) => symbolMap.get(symbolId)?.[pairIndex] ?? symbolId).toReversed();
    });
};

export const mergeDoubleReels = (symbols: number[][], startId: number = 9): number[][] => {
    if (symbols.length % 2 !== 0) {
        throw new Error('Expected an even number of reels');
    }

    return symbols
        .filter((_, reelIndex) => reelIndex % 2 === 0)
        .map((leftReel, pairIndex) => {
            const rightReel = symbols[pairIndex * 2 + 1];

            if (leftReel.length !== rightReel.length) {
                throw new Error('Paired reels must have the same length');
            }

            return leftReel
                .map((leftId, symbolIndex) => {
                    const rightId = rightReel[symbolIndex];

                    const left = doubleCoinIdsArray.findIndex((id) => id === leftId);
                    const right = doubleCoinIdsArray.findIndex((id) => id === rightId);

                    if (left !== -1 && right !== -1) {
                        return encodeDoubleCoinSymbolId([left, right], startId);
                    }

                    if (left === -1 && right === -1) return leftId;

                    if (left === -1 && right !== -1) {
                        return encodeDoubleCoinSymbolId([0, right], startId);
                    }
                    if (right === -1 && left !== -1) {
                        return encodeDoubleCoinSymbolId([left, 0], startId);
                    }

                    if (leftId === rightId) return leftId;

                    throw new Error(`Unknown symbol pair: [${leftId}, ${rightId}]`);
                })
                .toReversed();
        });
};

export const normalizeStartOrFinalSymbols = (symbols: number[][]): number[][] => {
    return filterEmptyReels(symbols).map((reel) => reel.toReversed());
};

export const getVisibleSymbolsFromFinalSymbols = (finalSymbols: number[][]): number[][] => {
    return finalSymbols.map((reel) => reel.slice(1, -1));
};

export const normalizeWinningLine = (winningLine: number[]): number[] => {
    return winningLine.map((line) => line - 1);
};

export const normalizeWaysPositions = (waysPositions: [number, number][]): [number, number][] => {
    return waysPositions.map(([reelIndex, symbolIndex]) => [reelIndex, symbolIndex - 1] as [number, number]);
};

export const normalizeScatterPositions = (scatterPositions: [number, number][], numberOfSymbolsPerReel: number): [number, number][] => {
    return scatterPositions.map(([reelIndex, symbolIndex]) => [reelIndex, numberOfSymbolsPerReel - symbolIndex] as [number, number]);
};

export const getNormalizedLockAndRollCoinPositions = (lockAndRollCoins: ILockAndRollSymbolData[], numberOfSymbolsPerReel: number): [number, number][] => {
    return lockAndRollCoins.map((lockAndRollCoin) => [lockAndRollCoin.reel, numberOfSymbolsPerReel - 1 - lockAndRollCoin.index] as [number, number]);
};

export const filterCoinPositionsForDoubleCoins = (lockAndRollPositions: [number, number][]): [number, number][] => {
    const merged = lockAndRollPositions.map(([reel, row]): [number, number] => [Math.floor(reel / 2), row]);

    const seen = new Set<string>();
    const deduped: [number, number][] = [];

    for (const [reel, row] of merged) {
        const key = `${reel}_${row}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push([reel, row]);
    }

    return deduped;
};
